import React from 'react';

/**
 * UserAvatar — shared avatar component used everywhere initials are shown.
 *
 * Props:
 *   user   — object with { logo_url, full_name, email }
 *   size   — 'sm' (24px) | 'md' (32px) | 'lg' (56px) | number (px)
 *   shape  — 'circle' (default)
 *   className — extra classes
 */

const SIZE_MAP = {
  sm: 24,
  md: 32,
  lg: 56,
};

const getInitials = (fullName, email) => {
  if (fullName) {
    const parts = fullName?.trim()?.split(' ')?.filter(Boolean);
    if (parts?.length >= 2) {
      return (parts?.[0]?.[0] + parts?.[parts?.length - 1]?.[0])?.toUpperCase();
    }
    return parts?.[0]?.[0]?.toUpperCase();
  }
  if (email) return email?.[0]?.toUpperCase();
  return 'U';
};

const UserAvatar = ({ user, size = 'md', shape = 'circle', className = '' }) => {
  const px = typeof size === 'number' ? size : (SIZE_MAP?.[size] ?? 32);
  const fontSize = px <= 24 ? '9px' : px <= 32 ? '11px' : '20px';
  const fontWeight = px <= 32 ? '600' : '500';

  const initials = getInitials(user?.full_name, user?.email);
  const logoUrl = user?.logo_url;

  const shapeClass = shape === 'circle' ? 'rounded-full' : 'rounded-lg';

  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={user?.full_name || user?.email || 'Avatar'}
        className={`object-cover flex-shrink-0 ${shapeClass} ${className}`}
        style={{ width: px, height: px, minWidth: px, minHeight: px }}
        onError={(e) => {
          // Fallback: hide img and let parent re-render won't happen, so we swap to initials
          e.currentTarget.style.display = 'none';
          const sibling = e?.currentTarget?.nextSibling;
          if (sibling) sibling.style.display = 'flex';
        }}
      />
    );
  }

  return (
    <div
      className={`flex items-center justify-center flex-shrink-0 bg-orange-100 ${shapeClass} ${className}`}
      style={{ width: px, height: px, minWidth: px, minHeight: px }}
    >
      <span style={{ fontSize, fontWeight, color: '#FF6B2C', lineHeight: 1 }}>
        {initials}
      </span>
    </div>
  );
};

export default UserAvatar;
